import Question from '@evidentid/ido-lib/interfaces/Question';

/**
 * The language to render the Incode flow in, from the language the session resolved.
 *
 * The region is dropped. Incode and Weglot do not offer the same set: Weglot carries
 * the regional tags this product standardises on, while Incode has only a handful of
 * its own (`fr-CA`, `es-MX`, `pt-BR`, ...) and none of ours. Passing a tag it does not
 * know falls back to English, so a Spanish page would render an English capture flow.
 *
 * Every base our languages reduce to -- en, de, es, fr, it, nl -- is one Incode
 * supports, so the region is the only part worth losing. The cost is that Canadian
 * French gets generic French here, which Incode could have served as `fr-CA`; a table
 * mapping each tag onto Incode's own spelling would recover that, at the price of
 * drifting whenever either vendor changes its list.
 */
export function getIncodeLanguage(tag: string): string {
    return tag.split('-')[0];
}

// BE maps these enum strings to environment-specific UUIDs, so we never hardcode UUIDs here.
export function getDocumentScanConfigId(): string {
    return 'JUST_ID_SCAN';
}

export function getLivenessConfigId(): string {
    return 'ID_SCAN_AND_LIVENESS';
}

export function getUsOnlyDocumentScanConfigId(): string {
    return 'JUST_ID_SCAN_US_ONLY';
}

export function getUsOnlyLivenessConfigId(): string {
    return 'ID_SCAN_AND_LIVENESS_US_ONLY';
}

const US_DOCUMENT_ATTR_PREFIX = 'identity_assurance.document_verification.americas.us';

// Checks both attrType (document-only path, where the US doc question survives curation)
// and metadata.isUsOnly (liveness path, where the US doc attrs are merged into the liveness
// question by getSessionInterview — see that file for why pre-curation detection is needed).
function isUsOnlyQuestion(question: Question): boolean {
    return question.attrType.startsWith(US_DOCUMENT_ATTR_PREFIX) || Boolean(question.metadata?.isUsOnly);
}

/**
 * Returns true if the request contains any US identity document attribute,
 * indicating the US-only Incode flow should be used.
 */
export function isUsOnlyRequest(questions: Question[]): boolean {
    return questions.some(isUsOnlyQuestion);
}

/**
 * Returns true when this question's tile should display the US accepted-documents list.
 * Guards on question.type === 'liveness-incode' so the list only shows on Incode questions.
 */
export function isUsIncodeDocumentQuestion(question: Question): boolean {
    return question.type === 'liveness-incode' && isUsOnlyQuestion(question);
}

export const US_ACCEPTED_DOCUMENTS = [
    'United States Driver\'s License',
    'United States ID Card',
    'United States Passport',
    'United States Permanent Residence Card',
] as const;
